import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Shield, FileText, Users, Database, Settings, AlertTriangle, CheckCircle, Clock, Download, Eye, Lock } from 'lucide-react';
import api from '../api';

interface AuditLog {
  id: string;
  timestamp: string;
  user_email: string;
  user_name: string;
  action: string;
  action_category: string;
  event_category: 'AUTH' | 'DATA' | 'TRANSACTION' | 'CONFIG' | 'REPORTING' | 'PRIVILEGED';
  resource_type?: string;
  resource_name?: string;
  client_name?: string;
  status: 'SUCCESS' | 'FAILURE' | 'WARNING' | 'BLOCKED';
  risk_level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  ip_address?: string;
  error_message?: string;
  compliance_tags: string[];
  regulatory_significance: boolean;
  business_justification?: string;
  retention_period: number;
  table_name?: string;
  record_id?: string;
  old_values?: any;
  new_values?: any;
  session_id?: string;
  approved_by?: string;
  approval_required: boolean;
}

interface AuditFilters {
  startDate?: string;
  endDate?: string;
  actionCategory?: string;
  eventCategory?: string;
  riskLevel?: string;
  status?: string;
  clientName?: string;
  userId?: string;
  regulatorySignificance?: boolean;
  approvalRequired?: boolean;
  retentionPeriod?: string;
  tableName?: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  recordsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const AuditDashboard: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null); // Track which format is being exported
  const [filters, setFilters] = useState<AuditFilters>({});
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 50,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [statistics, setStatistics] = useState<any>({});
  const [regulatoryView, setRegulatoryView] = useState<'all' | 'sox' | 'basel' | 'aml' | 'gdpr' | 'privileged'>('all');
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d' | '90d' | '1y'>('30d');
  const [users, setUsers] = useState<Array<{ id: string; email: string; name: string }>>([]);

  // Debug: Log component mount and users state changes
  useEffect(() => {
    console.log('🔍 AuditDashboard mounted');
  }, []);

  useEffect(() => {
    console.log('👥 Users state updated:', users.length, 'users', users);
  }, [users]);

  // Memoize filters to prevent unnecessary re-renders
  const memoizedFilters = useMemo(() => {
    return JSON.stringify(filters);
  }, [filters]);

  // Fetch audit logs
  const fetchAuditLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', pagination.recordsPerPage.toString());
      
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.actionCategory) params.append('actionCategory', filters.actionCategory);
      if (filters.eventCategory) params.append('eventCategory', filters.eventCategory);
      if (filters.riskLevel) params.append('riskLevel', filters.riskLevel);
      if (filters.status) params.append('status', filters.status);
      if (filters.clientName) params.append('clientName', filters.clientName);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.regulatorySignificance !== undefined) params.append('regulatorySignificance', filters.regulatorySignificance.toString());
      if (filters.approvalRequired !== undefined) params.append('approvalRequired', filters.approvalRequired.toString());
      if (filters.retentionPeriod) params.append('retentionPeriod', filters.retentionPeriod);
      if (filters.tableName) params.append('tableName', filters.tableName);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050';
      const response = await fetch(`${apiUrl}/api/audit/logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jarvis_access_token')}`
        }
      });
      console.log("response of the audit logs ",response)

      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.auditLogs);
        setPagination(data.pagination);
      } else {
        console.error('Failed to fetch audit logs:', response.statusText);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.actionCategory,
    filters.eventCategory,
    filters.riskLevel,
    filters.status,
    filters.clientName,
    filters.userId,
    filters.regulatorySignificance,
    filters.approvalRequired,
    filters.retentionPeriod,
    filters.tableName,
    pagination.recordsPerPage
  ]);

  // Fetch dashboard statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const daysMap = { '24h': 1, '7d': 7, '30d': 30, '90d': 90, '1y': 365 };
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050';
      const response = await fetch(`${apiUrl}/api/audit/dashboard?days=${daysMap[timeRange]}&regulatoryView=${regulatoryView}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jarvis_access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.statistics);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  }, [timeRange, regulatoryView]);

  // Export audit logs
  const exportAuditLogs = async (format: 'csv' | 'json') => {
    setExporting(format);
    try {
      console.log('Current filters for export:', filters);
      const response = await api.post('/api/audit/export', {
        startDate: filters.startDate,
        endDate: filters.endDate,
        format,
        filters: {
          actionCategory: filters.actionCategory,
          eventCategory: filters.eventCategory,
          riskLevel: filters.riskLevel,
          status: filters.status,
          clientName: filters.clientName,
          userId: filters.userId,
          regulatorySignificance: filters.regulatorySignificance,
          approvalRequired: filters.approvalRequired,
          retentionPeriod: filters.retentionPeriod,
          tableName: filters.tableName
        }
      }, {
        responseType: 'blob' // Important for file downloads
      });

      // Create blob and download
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(null);
    }
  };

  // Fetch users for dropdown
  const fetchUsers = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050';
      const response = await fetch(`${apiUrl}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jarvis_access_token')}`
        }
      });

      console.log('Fetch users response:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('Users data received:', data);
        setUsers(data.users || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to fetch users:', response.status, response.statusText, errorData);
      }
    } catch (error) {
      console.error('Failed to fetch users (exception):', error);
    }
  }, []);

  useEffect(() => {
    fetchAuditLogs();
    fetchStatistics();
    fetchUsers();
  }, [memoizedFilters, fetchAuditLogs, fetchStatistics, fetchUsers]);

  // Format action for better display
  const formatAction = (action: string) => {
    if (!action) return 'System Access';
    
    // If already well formatted, return as is
    if (action.includes(' ') && !action.includes('Unknown')) return action;
    
    // Handle specific bad cases
    if (action.includes('UNKNOWN') || action.includes('Unknown')) {
      return 'System Access';
    }
    
    // Convert snake_case or UPPER_CASE to Title Case
    return action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Format error message to be user-friendly
  const formatErrorMessage = (errorMessage: string) => {
    if (!errorMessage) return null;
    
    // Don't show JSON or technical errors to users
    try {
      JSON.parse(errorMessage);
      return 'Operation failed - please contact support';
    } catch {
      // Not JSON, check for other technical indicators
      if (errorMessage.includes('{') || errorMessage.includes('"') || errorMessage.includes('stack')) {
        return 'Operation failed - please contact support';
      }
      return errorMessage;
    }
  };


  // Format event category for enterprise display
  const formatEventCategory = (eventCategory: string) => {
    const eventCategoryMap: { [key: string]: { icon: string; label: string; description: string } } = {
      'AUTH': { icon: '🔐', label: 'Authentication & Access', description: 'Login, logout, role changes' },
      'DATA': { icon: '📊', label: 'Data Access & Modification', description: 'Record changes, queries, exports' },
      'TRANSACTION': { icon: '💰', label: 'Financial Transactions', description: 'Payments, transfers, calculations' },
      'CONFIG': { icon: '⚙️', label: 'System Configuration', description: 'Business rules, parameter changes' },
      'REPORTING': { icon: '📋', label: 'Regulatory Reporting', description: 'Compliance reports, submissions' },
      'PRIVILEGED': { icon: '🔒', label: 'Privileged Operations', description: 'Admin actions, emergency access' }
    };
    
    return eventCategoryMap[eventCategory] || { icon: '📝', label: eventCategory, description: 'System event' };
  };

  // Get regulatory framework badges
  const getRegulatoryBadges = (complianceTags: string[]) => {
    const regulatoryFrameworks = {
      'SOX': { color: 'bg-red-100 text-red-800', label: 'SOX' },
      'BASEL': { color: 'bg-blue-100 text-blue-800', label: 'Basel III' },
      'AML': { color: 'bg-purple-100 text-purple-800', label: 'AML/BSA' },
      'GDPR': { color: 'bg-green-100 text-green-800', label: 'GDPR' },
      'PCI': { color: 'bg-orange-100 text-orange-800', label: 'PCI DSS' },
      'FFIEC': { color: 'bg-indigo-100 text-indigo-800', label: 'FFIEC' }
    };
    
    return complianceTags.filter(tag => regulatoryFrameworks[tag as keyof typeof regulatoryFrameworks])
      .map(tag => regulatoryFrameworks[tag as keyof typeof regulatoryFrameworks]);
  };

  const getRiskBadgeStyle = (riskLevel: string) => {
    switch (riskLevel) {
      case 'CRITICAL': 
        return 'bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1';
      case 'HIGH': 
        return 'bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1';
      case 'MEDIUM': 
        return 'bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1';
      case 'LOW': 
        return 'bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1';
      default: 
        return 'bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1';
    }
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'CRITICAL': return <AlertTriangle className="w-3 h-3" />;
      case 'HIGH': return <AlertTriangle className="w-3 h-3" />;
      case 'MEDIUM': return <Eye className="w-3 h-3" />;
      case 'LOW': return <CheckCircle className="w-3 h-3" />;
      default: return <Shield className="w-3 h-3" />;
    }
  };

  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case 'SUCCESS': 
        return 'bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium';
      case 'FAILURE': 
        return 'bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium';
      case 'WARNING': 
        return 'bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium';
      case 'BLOCKED': 
        return 'bg-red-200 text-red-900 px-2 py-1 rounded text-xs font-medium';
      default: 
        return 'bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium';
    }
  };

  const updateFilter = (key: keyof AuditFilters, value: string) => {
    setFilters(prev => ({ 
      ...prev, 
      [key]: value === '' ? undefined : value 
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-super-dark-purple">Enterprise Audit Dashboard</h1>
          <p className="text-gray-600">Financial Institution Regulatory Compliance & Risk Monitoring</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => exportAuditLogs('csv')}
            disabled={exporting !== null}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              exporting === 'csv'
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <Download className="w-4 h-4" />
            {exporting === 'csv' ? 'Exporting...' : 'Export CSV'}
          </button>
          <button
            onClick={() => exportAuditLogs('json')}
            disabled={exporting !== null}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              exporting === 'json'
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <Download className="w-4 h-4" />
            {exporting === 'json' ? 'Exporting...' : 'Export JSON'}
          </button>
        </div>
      </div>

      {/* Regulatory Framework Selector */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Regulatory Compliance View
          </h3>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-500" />
            <select 
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value as any)}
              className="border border-gray-300 rounded px-3 py-1 text-sm"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="1y">Last Year</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All Events', icon: Shield },
            { key: 'sox', label: 'SOX Compliance', icon: FileText },
            { key: 'basel', label: 'Basel III', icon: Database },
            { key: 'aml', label: 'AML/BSA', icon: Users },
            { key: 'gdpr', label: 'GDPR', icon: Lock },
            { key: 'privileged', label: 'Privileged Access', icon: Settings }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setRegulatoryView(key as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                regulatoryView === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border rounded-lg p-4 shadow">
          <div className="text-2xl font-bold">{statistics.totalEvents || 0}</div>
          <div className="text-sm text-gray-600">Total Events (30 days)</div>
        </div>
        <div className="bg-white border rounded-lg p-4 shadow">
          <div className="text-2xl font-bold text-red-600">
            {statistics.securityEventsCount || 0}
          </div>
          <div className="text-sm text-gray-600">Security Events</div>
        </div>
        <div className="bg-white border rounded-lg p-4 shadow">
          <div className="text-2xl font-bold text-yellow-600">
            {statistics.failedOperationsCount || 0}
          </div>
          <div className="text-sm text-gray-600">Failed Operations</div>
        </div>
        <div className="bg-white border rounded-lg p-4 shadow">
          <div className="text-2xl font-bold text-blue-600">
            {Object.keys(statistics.byUser || {}).length}
          </div>
          <div className="text-sm text-gray-600">Active Users</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-6 shadow">
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Date Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={filters.startDate || ''}
              onChange={(e) => updateFilter('startDate', e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={filters.endDate || ''}
              onChange={(e) => updateFilter('endDate', e.target.value)}
            />
          </div>

          {/* User Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
            <select
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={filters.userId || ''}
              onChange={(e) => updateFilter('userId', e.target.value)}
            >
              <option value="">All Users</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>

          {/* Action Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={filters.actionCategory || ''}
              onChange={(e) => updateFilter('actionCategory', e.target.value)}
            >
              <option value="">All Categories</option>
              <option value="AUTHENTICATION">Authentication</option>
              <option value="DOCUMENT_MANAGEMENT">Documents</option>
              <option value="PROJECT_MANAGEMENT">Projects</option>
              <option value="ANALYSIS">Analysis</option>
              <option value="EXPORT">Exports</option>
              <option value="SECURITY">Security</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>

          {/* Risk Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label>
            <select
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={filters.riskLevel || ''}
              onChange={(e) => updateFilter('riskLevel', e.target.value)}
            >
              <option value="">All Risk Levels</option>
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={filters.status || ''}
              onChange={(e) => updateFilter('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILURE">Failure</option>
              <option value="WARNING">Warning</option>
              <option value="BLOCKED">Blocked</option>
            </select>
          </div>

          {/* Client Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
            <input
              type="text"
              placeholder="Client Name"
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={filters.clientName || ''}
              onChange={(e) => updateFilter('clientName', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Audit Logs */}
      <div className="bg-white border rounded-lg p-6 shadow">
        <h3 className="text-lg font-semibold mb-4">Audit Logs</h3>
        
        {loading ? (
          <div className="text-center py-8">Loading audit logs...</div>
        ) : (
          <div className="space-y-4">
            {auditLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No audit logs found for the selected filters.
              </div>
            ) : (
              auditLogs.map((log) => (
                <div key={log.id} className="border rounded-lg p-4 space-y-3 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-lg text-gray-800">
                          {formatAction(log.action)}
                        </div>
                        {log.event_category && (
                          <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                            log.event_category === 'AUTH' ? 'bg-blue-100 text-blue-800' :
                            log.event_category === 'DATA' ? 'bg-green-100 text-green-800' :
                            log.event_category === 'TRANSACTION' ? 'bg-purple-100 text-purple-800' :
                            log.event_category === 'CONFIG' ? 'bg-orange-100 text-orange-800' :
                            log.event_category === 'REPORTING' ? 'bg-indigo-100 text-indigo-800' :
                            log.event_category === 'PRIVILEGED' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {formatEventCategory(log.event_category).label}
                          </span>
                        )}
                        {log.regulatory_significance && (
                          <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800 font-bold">
                            REGULATORY
                          </span>
                        )}
                        {/* {log.approval_required && (
                          <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-800 font-medium">
                            APPROVAL REQ
                          </span>
                        )} */}
                      </div>
                      
                      <div className="text-sm text-gray-600 flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          👤 {log.user_name || log.user_email}
                        </span>
                        <span className="flex items-center gap-1">
                          ⏰ {new Date(log.timestamp).toLocaleString()}
                        </span>
                        {/* {log.ip_address && (
                          <span className="flex items-center gap-1">
                            🌐 {log.ip_address}
                          </span>
                        )} */}
                      </div>                   
                       {(log.resource_name || log.client_name) && (
                        <div className="text-sm space-y-1">
                           {/* {log.resource_name && (
                            <div className="flex items-center gap-1">
                              <span className="font-medium text-gray-700">{log.resource_type}:</span> 
                              <span className="text-gray-600">{log.resource_name}</span>
                           </div>
                         )} */}
                         {log.client_name && (
                           <div className="flex items-center gap-1">
                           <span className="font-medium text-gray-700">Client:</span> 
                           <span className="text-gray-600">{log.client_name}</span>
                            </div>
                          )}
                        </div>
                     )}
                      
                      {log.error_message && formatErrorMessage(log.error_message) && (
                        <div className="text-sm text-red-600 bg-red-50 p-2 rounded flex items-center gap-2">
                          ⚠️ <span className="font-medium">Error:</span> {formatErrorMessage(log.error_message)}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2 items-end">
                      <span className={getRiskBadgeStyle(log.risk_level)}>
                        {getRiskIcon(log.risk_level)}
                        {log.risk_level}
                      </span>
                      <span className={getStatusBadgeStyle(log.status)}>
                        {log.status}
                      </span>
                      {log.retention_period && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          Retain {Math.floor(log.retention_period / 365)}y
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Enhanced Compliance & Regulatory Section */}
                  {((log.compliance_tags && log.compliance_tags.length > 0) || log.business_justification || log.approved_by || (log.old_values || log.new_values)) && (
                    <div className="border-t pt-3 space-y-3">
                      {/* Regulatory Framework Badges */}
                      {log.compliance_tags && log.compliance_tags.length > 0 && (
                        <div>
                          <div className="text-xs text-gray-500 mb-2 font-medium">Regulatory Frameworks:</div>
                          <div className="flex gap-2 flex-wrap">
                            {getRegulatoryBadges(log.compliance_tags).map((badge, index) => (
                              <span 
                                key={index} 
                                className={`${badge.color} text-xs px-3 py-1 rounded-full font-medium border`}
                              >
                                {badge.label}
                              </span>
                            ))}
                            {log.compliance_tags.filter(tag => !getRegulatoryBadges([tag]).length).map((tag, index) => (
                              <span 
                                key={index} 
                                className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded border"
                              >
                                {tag.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Business Justification */}
                      {log.business_justification && (
                        <div className="bg-blue-50 p-3 rounded">
                          <div className="text-xs text-blue-600 font-medium mb-1">Business Justification:</div>
                          <div className="text-sm text-blue-800">{log.business_justification}</div>
                        </div>
                      )}
                      
                      {/* Approval Information */}
                      {log.approved_by && (
                        <div className="bg-green-50 p-3 rounded">
                          <div className="text-xs text-green-600 font-medium mb-1">Approved By:</div>
                          <div className="text-sm text-green-800">{log.approved_by}</div>
                        </div>
                      )}
                      
                      {/* Action Performed */}
                      {(log.old_values || log.new_values) && (
                        <div className="bg-gray-50 p-3 rounded">
                          <div className="text-xs text-gray-600 font-medium mb-2">Action Performed:</div>
                          <div className="grid grid-cols-1 gap-3">
                            {log.old_values && (
                              <div>
                                <div className="text-xs text-red-600 font-medium mb-1">Before:</div>
                                <pre className="text-xs text-red-700 bg-red-50 p-2 rounded overflow-auto max-h-20">
                                  {JSON.stringify(log.old_values, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log.new_values && (
                              <div>
                                <div className="text-xs text-green-600 font-medium mb-1">After:</div>
                                <pre className="text-xs text-green-700 bg-green-50 p-2 rounded overflow-auto max-h-20">
                                  {JSON.stringify(log.new_values, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
            
            {/* Pagination */}
            {auditLogs.length > 0 && (
              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm text-gray-600">
                  Showing {((pagination.currentPage - 1) * pagination.recordsPerPage) + 1} to{' '}
                  {Math.min(pagination.currentPage * pagination.recordsPerPage, pagination.totalRecords)} of{' '}
                  {pagination.totalRecords} records
                </div>
                <div className="flex gap-2">
                  <button
                    className={`px-4 py-2 rounded border ${
                      pagination.currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    disabled={pagination.currentPage === 1}
                    onClick={() => fetchAuditLogs(pagination.currentPage - 1)}
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-600">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                  <button
                    className={`px-4 py-2 rounded border ${
                      pagination.currentPage === pagination.totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    disabled={pagination.currentPage === pagination.totalPages}
                    onClick={() => fetchAuditLogs(pagination.currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditDashboard;